import { cn } from '../../../lib/utils';
import type { ExternalIntelBand } from '../../../api/intelClient';

interface BandChipProps {
  band: ExternalIntelBand;
  className?: string;
}

/**
 * Achromatic band chip — cycle-105 sprint-1 Task 3.
 *
 * All 5 bands render via an opacity + weight ramp on `--e-text-primary`.
 * No hue. Echelon has not endorsed the WorldMonitor severity, so chroma stays
 * at zero for the state channel. Property 14 (Simplicity & Inner Calm):
 * restraint over decoration.
 *
 * The single chromatic element on the External Intelligence panel is the
 * EXTERNAL · UNVERIFIED BY ECHELON badge in the panel header (iris). All
 * row-level state is achromatic.
 *
 * No numeric values render in the DOM, even via title or aria — the
 * backend strips `composite_value` from the response, and this component
 * never accepts it as a prop. AC: `grep -r "compositeValue" frontend/src`
 * returns 0 matches outside the band-mapper utility (this file is the
 * band-mapper).
 */
export function BandChip({ band, className }: BandChipProps) {
  return (
    <span
      data-testid={`external-intel-band-${band.toLowerCase()}`}
      data-band={band}
      role="status"
      aria-label={`External intelligence band: ${band}`}
      className={cn(
        // Base — fixed geometry across all bands so the eye reads weight
        // and not size as the severity signal.
        'inline-flex items-center justify-center',
        'min-w-[3.5rem] px-2 py-0.5',
        'rounded-sm text-[10px] font-mono uppercase tracking-wider',
        'border',
        // Per-band ramp — opacity on `--e-text-primary`, varying weight.
        BAND_STYLES[band],
        className,
      )}
    >
      {band}
    </span>
  );
}

/** Achromatic ramp. The chip's structural rhythm is fixed; the eye reads
 * weight + fill density, not hue. */
const BAND_STYLES: Record<ExternalIntelBand, string> = {
  Low: cn(
    'font-normal',
    'border-[var(--e-border-secondary)]/60',
    'text-[var(--e-text-secondary)]',
    'bg-transparent',
  ),
  Elevated: cn(
    'font-medium',
    'border-[var(--e-border-secondary)]',
    'text-[var(--e-text-primary)]',
    'bg-[var(--e-text-primary)]/[0.06]',
  ),
  High: cn(
    'font-semibold',
    'border-[var(--e-border-primary)]',
    'text-[var(--e-text-primary)]',
    'bg-[var(--e-text-primary)]/[0.12]',
  ),
  Critical: cn(
    'font-bold',
    'border-[var(--e-border-primary)]',
    'text-[var(--e-text-primary)]',
    'bg-[var(--e-text-primary)]/[0.18]',
  ),
  unavailable: cn(
    'font-normal italic',
    'border-[var(--e-border-secondary)]/40 border-dashed',
    'text-[var(--e-text-muted)]',
    'bg-transparent',
  ),
};
